// Screen Wake Lock helper.
//
// Keeps the device screen on while a workout timer is open so phones
// don't auto-sleep mid-MetCon. Uses the Screen Wake Lock API
// (https://developer.mozilla.org/en-US/docs/Web/API/Screen_Wake_Lock_API),
// which is supported on all modern Chrome/Edge/Firefox/Safari (iOS 16.4+).
//
// The browser auto-releases the lock when the tab is hidden or the
// device is locked, so we re-request it on `visibilitychange` whenever
// the user has wake-lock enabled.

import { useCallback, useEffect, useRef, useState } from "react";

export const wakeLockSupported = () =>
  typeof navigator !== "undefined" && "wakeLock" in navigator;

/**
 * useWakeLock(active)
 *
 * @param {boolean} active   When true, hold a screen wake-lock; when false, release it.
 * @returns {{ supported: boolean, locked: boolean, error: string | null }}
 */
export function useWakeLock(active) {
  const sentinelRef = useRef(null);
  const [locked, setLocked] = useState(false);
  const [error, setError] = useState(null);
  const supported = wakeLockSupported();

  const release = useCallback(async () => {
    const sentinel = sentinelRef.current;
    sentinelRef.current = null;
    setLocked(false);
    if (sentinel) {
      try { await sentinel.release(); } catch { /* noop */ }
    }
  }, []);

  const acquire = useCallback(async () => {
    if (!supported) return;
    if (sentinelRef.current) return;
    try {
      const sentinel = await navigator.wakeLock.request("screen");
      sentinelRef.current = sentinel;
      setLocked(true);
      setError(null);
      sentinel.addEventListener("release", () => {
        // Browser released it (tab hidden, screen lock, etc.)
        if (sentinelRef.current === sentinel) {
          sentinelRef.current = null;
          setLocked(false);
        }
      });
    } catch (e) {
      setError(e?.message || "Could not acquire wake lock");
      setLocked(false);
    }
  }, [supported]);

  // React to `active` flag.
  useEffect(() => {
    if (active) acquire();
    else release();
    return () => { release(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  // Re-acquire when the tab regains visibility while still active.
  useEffect(() => {
    if (!supported) return undefined;
    const onVis = () => {
      if (active && document.visibilityState === "visible" && !sentinelRef.current) {
        acquire();
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [active, acquire, supported]);

  return { supported, locked, error };
}
